import React, { useState } from "react";
import { Autocomplete, Box, TextField, Checkbox, Chip } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import CheckedIcon from "../../svg/CheckedIcon";
import UncheckedIcon from "../../svg/UncheckedIcon";
import { CustomPopper } from "./CustomAutoComplete";
import InfoIcon from "../../svg/InfoIcon";
import CustomTooltip from "./CustomTooltip";

const CustomArrowIcon = ({ focused }: { focused: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M7.99943 11.0006C7.73424 11.0005 7.47992 10.8951 7.29243 10.7076L3.29243 6.70757C3.11027 6.51897 3.00948 6.26636 3.01176 6.00417C3.01403 5.74197 3.1192 5.49116 3.30461 5.30575C3.49002 5.12034 3.74083 5.01517 4.00303 5.0129C4.26523 5.01062 4.51783 5.11141 4.70643 5.29357L7.99943 8.58657L11.2924 5.29357C11.3847 5.19806 11.495 5.12188 11.617 5.06947C11.739 5.01706 11.8703 4.98947 12.003 4.98832C12.1358 4.98717 12.2675 5.01247 12.3904 5.06275C12.5133 5.11303 12.6249 5.18728 12.7188 5.28117C12.8127 5.37507 12.887 5.48672 12.9373 5.60962C12.9875 5.73251 13.0128 5.86419 13.0117 5.99697C13.0105 6.12975 12.9829 6.26097 12.9305 6.38297C12.8781 6.50498 12.8019 6.61532 12.7064 6.70757L8.70643 10.7076C8.51894 10.8951 8.26463 11.0005 7.99943 11.0006Z"
      fill={focused ? "#1064DB" : "#3B3F42"}
    />
  </svg>
);

type OptionType = string | { metric: string; benefit: string; [key: string]: any };

const CustomMultiSelectAutoComplete = (props: {
  label?: string;
  placeholder?: string;
  options: OptionType[];
  internalValue: OptionType[];
  setInternalValue: (value: OptionType[]) => void;
  showTooltip?: boolean;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const { internalValue, setInternalValue } = props;

  const isDisabled = props.options.length === 0;

  return (
    <Box sx={{ flex: 1, position: "relative" }}>
      <Autocomplete
        multiple
        disabled={isDisabled}
        value={internalValue}
        onChange={(event: any, newValue: string[]) => {
          setInternalValue(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        options={props.options}
        disableCloseOnSelect
        disableClearable
        getOptionLabel={(option) => typeof option === 'string' ? option : option.metric}
        popupIcon={<CustomArrowIcon focused={isFocused && !isDisabled} />}
        PopperComponent={CustomPopper}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={typeof option === 'string' ? option : option.metric}
              sx={{
                backgroundColor: theme.colorTokens.N[30],
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                borderRadius: "8px",
                marginTop: "8px",
              }}
            />
          ))
        }
        renderOption={(renderProps, option, { selected }) => {
          const { key, ...optionProps } = renderProps;
          const isObjectOption = typeof option === 'object' && option.metric;
          const displayText = typeof option === 'string' ? option : option.metric;
          
          return (
            <li
              key={key}
              {...optionProps}
              style={{
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                lineHeight: "17px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Checkbox
                  icon={
                    <UncheckedIcon fill={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  }
                  checkedIcon={
                    <CheckedIcon fill={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  }
                  sx={{
                    marginRight: 1,
                    padding: 0,
                    width: "20px",
                    height: "20px",
                    "& .MuiSvgIcon-root": {
                      width: "20px",
                      height: "20px",
                    },
                  }}
                  checked={selected}
                />
                <Box sx={{ flex: 1 }}>{displayText}</Box>
              </Box>
              {isObjectOption && props.showTooltip && option.benefit && (
                <CustomTooltip message={option.benefit} customHeight="auto">
                  <InfoIcon />
                </CustomTooltip>
              )}
            </li>
          );
        }}
        sx={{
          width: "100%",
          "& .MuiAutocomplete-popupIndicator": {
            padding: "4px",
            "&:hover": {
              borderRadius: "50%",
            },
          },
          "& .MuiInputLabel-root": {
            fontFamily: "var(--font-Medium)",
            fontSize: "10px",
            backgroundColor: "transparent",
            transition: "transform 0.2s ease-in-out",
            color: isDisabled
              ? theme.colors_ds.NEUTRAL_TEXT_WEAK
              : theme.colors_ds.NEUTRAL_TEXT_WEAK,
            transform: Boolean(
              inputValue || isFocused || internalValue.length > 0
            )
              ? "translate(12px, 6px) scale(1)"
              : "translate(12px, 16px) scale(1.5)",
            "&.Mui-focused": {
              color: isDisabled
                ? theme.colors_ds.NEUTRAL_TEXT_WEAK
                : theme.colors_ds.PRIMARY_ICON_MAIN,
            },
            "&.Mui-disabled": {
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
            },
          },
          "& .MuiOutlinedInput-root": {
            minHeight: "56px",
            borderRadius: "8px",
            backgroundColor: isDisabled
              ? theme.colorTokens.N[20]
              : "transparent",
            "& input": {
              caretColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              color: isDisabled
                ? theme.colors_ds.NEUTRAL_TEXT_WEAK
                : theme.colors_ds.NEUTRAL_TEXT_STRONG,
            },
            "& fieldset": {
              borderColor: isDisabled
                ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                : theme.colors_ds.NEUTRAL_BORDER_WEAK,
              top: 0,
            },
            "& legend": {
              display: "none",
            },
            "&:hover fieldset": {
              borderColor: isDisabled
                ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
            },
            "&.Mui-focused fieldset": {
              borderColor: isDisabled
                ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                : theme.colors_ds.PRIMARY_ICON_MAIN,
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.colors_ds.NEUTRAL_BG_WEAK,
              "& fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
              },
            },
          },
          "& .MuiInputBase-root": {
            paddingTop: internalValue.length > 0 ? "24px" : "16px",
          },
          "& .MuiOutlinedInput-input": {
            marginTop: "0px",
          },
        }}
        slotProps={{
          listbox: {
            sx: {
              maxHeight: "200px",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              },
              "& .MuiAutocomplete-option": {
                fontFamily: "var(--font-Medium)",
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                fontSize: "14px",
                padding: "8px 16px",
                "&:hover": {
                  backgroundColor: theme.colorTokens.N[40],
                },
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label ?? "Select"}
            placeholder={props.placeholder}
            onFocus={() => !isDisabled && setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& input": {
                  color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                  fontFamily: "var(--font-Medium)",
                  fontSize: "12px",
                  lineHeight: "20px",
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default CustomMultiSelectAutoComplete;
